import { useCallback, useEffect, useRef, useState } from "react";
import { ImagePlus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fileUrl, GALLERY_CATEGORIES, VISUAL_MAP, VISUAL_OPTIONS } from "@/lib/galleryVisuals";

const field =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-ink outline-none transition-colors focus:border-brand-blue/50";
const labelCls = "text-[11px] font-bold uppercase tracking-[0.14em] text-ink/45";

function Thumb({ item }) {
    if (item.image_path) {
        return <img src={fileUrl(item.image_path)} alt={item.title} className="h-full w-full object-cover" />;
    }
    const Visual = VISUAL_MAP[item.visual_key];
    return Visual ? (
        <div className="h-full w-full bg-gradient-to-br from-[#F6F7FB] to-[#EDF0F7] [&_svg]:h-full [&_svg]:w-full">
            <Visual />
        </div>
    ) : (
        <div className="flex h-full w-full items-center justify-center bg-slate-100 text-xs font-semibold text-ink/40">No visual</div>
    );
}

function GalleryCard({ item, onSave, onDelete, isNew }) {
    const [form, setForm] = useState(item);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const dirty = JSON.stringify(form) !== JSON.stringify(item);

    const uploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/api/admin/upload", fd);
            setForm((f) => ({ ...f, image_path: data.path }));
            toast.success("Photo uploaded — save to publish");
        } catch (err) {
            toast.error(apiError(err, "Photo upload failed"));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const save = async () => {
        if (!form.title.trim()) return toast.error("Title is required");
        if (!form.image_path && !form.visual_key) return toast.error("Upload a photo or choose an illustration");
        setBusy(true);
        try {
            await onSave(form);
            toast.success(isNew ? "Gallery item added" : "Gallery item updated");
        } catch (err) {
            toast.error(apiError(err, "Failed to save item"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={`gallery-admin-item-${item.id || "new"}`} className="rounded-2xl border border-slate-200/80 bg-white p-5">
            <div className="h-44 overflow-hidden rounded-xl border border-slate-200/70">
                <Thumb item={form} />
            </div>
            <div className="mt-4 space-y-3.5">
                <div>
                    <label className={labelCls}>Title</label>
                    <input data-testid={`gallery-title-${item.id || "new"}`} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={field} />
                </div>
                <div>
                    <label className={labelCls}>Category</label>
                    <select
                        data-testid={`gallery-category-${item.id || "new"}`}
                        value={form.category}
                        onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                        className={`${field} cursor-pointer`}
                    >
                        {GALLERY_CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>
                {!form.image_path && (
                    <div>
                        <label className={labelCls}>Illustration (used until a photo is uploaded)</label>
                        <select
                            data-testid={`gallery-visual-${item.id || "new"}`}
                            value={form.visual_key || ""}
                            onChange={(e) => setForm((f) => ({ ...f, visual_key: e.target.value || null }))}
                            className={`${field} cursor-pointer`}
                        >
                            <option value="">— None —</option>
                            {VISUAL_OPTIONS.map((v) => (
                                <option key={v.key} value={v.key}>{v.label}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-2.5 pt-1">
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} />
                    <button
                        data-testid={`gallery-upload-${item.id || "new"}`}
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-ink/70 transition-colors hover:border-brand-blue/50 hover:text-ink disabled:opacity-50"
                    >
                        <ImagePlus size={13} strokeWidth={2.5} />
                        {uploading ? "Uploading…" : form.image_path ? "Replace Photo" : "Upload Photo"}
                    </button>
                    {form.image_path && (
                        <button
                            data-testid={`gallery-remove-photo-${item.id || "new"}`}
                            onClick={() => setForm((f) => ({ ...f, image_path: null }))}
                            className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-ink/60 transition-colors hover:border-red-300 hover:text-red-600"
                        >
                            Remove Photo
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2.5 border-t border-slate-100 pt-3.5">
                    <button
                        data-testid={`gallery-save-${item.id || "new"}`}
                        onClick={save}
                        disabled={busy || (!dirty && !isNew)}
                        className="btn-gradient rounded-full px-5 py-2 text-xs font-bold text-white disabled:opacity-50"
                    >
                        {busy ? "Saving…" : isNew ? "Add Item" : "Save"}
                    </button>
                    {onDelete && (
                        <button
                            data-testid={`gallery-delete-${item.id}`}
                            onClick={onDelete}
                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-ink/60 transition-colors hover:border-red-300 hover:text-red-600"
                        >
                            <Trash2 size={13} strokeWidth={2.5} />
                            Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function GalleryTab() {
    const [items, setItems] = useState(null);
    const [adding, setAdding] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/api/gallery");
            setItems(data.items);
        } catch (e) {
            toast.error(apiError(e, "Failed to load gallery"));
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const saveItem = async (form, existing) => {
        const payload = {
            title: form.title,
            category: form.category,
            visual_key: form.image_path ? null : form.visual_key || null,
            image_path: form.image_path || null,
            featured: form.featured || false,
        };
        if (existing) {
            await api.put(`/api/admin/gallery/${existing.id}`, payload);
        } else {
            await api.post("/api/admin/gallery", payload);
            setAdding(false);
        }
        await load();
    };

    const deleteItem = async (item) => {
        if (!window.confirm(`Delete "${item.title}" from the gallery?`)) return;
        try {
            await api.delete(`/api/admin/gallery/${item.id}`);
            toast.success("Gallery item deleted");
            await load();
        } catch (e) {
            toast.error(apiError(e, "Failed to delete item"));
        }
    };

    if (!items) {
        return (
            <div className="flex justify-center py-20" data-testid="gallery-loading">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-blue" />
            </div>
        );
    }

    return (
        <div data-testid="gallery-tab">
            <div className="mb-5 flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-ink/55">
                    Upload real photos to replace the conceptual illustrations — they go live immediately after saving.
                </p>
                {!adding && (
                    <button
                        data-testid="add-gallery-item-btn"
                        onClick={() => setAdding(true)}
                        className="btn-gradient inline-flex shrink-0 items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-bold text-white"
                    >
                        <Plus size={14} strokeWidth={2.6} />
                        Add Item
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {adding && (
                    <GalleryCard
                        item={{ title: "", category: "Coworking", visual_key: "coworking", image_path: null, featured: false }}
                        isNew
                        onSave={(form) => saveItem(form, null)}
                        onDelete={() => setAdding(false)}
                    />
                )}
                {items.map((item) => (
                    <GalleryCard key={item.id} item={item} onSave={(form) => saveItem(form, item)} onDelete={() => deleteItem(item)} />
                ))}
            </div>
        </div>
    );
}
